import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Card, Flex, Heading, TextField, Text, Select } from '@radix-ui/themes'
import { useAuth } from '../context/AuthContext'
import { THOMSONS_LOGO, POS_BACKGROUND } from '../lib/assets'
import { toast } from 'react-toastify'
import { listClerks, type Clerk } from '../services/data'

export default function Login() {
  const [selectedClerkId, setSelectedClerkId] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [clerks, setClerks] = useState<Clerk[]>([])
  const [loadingClerks, setLoadingClerks] = useState(true)
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/'

  useEffect(() => {
    listClerks()
      .then(setClerks)
      .catch(e => { console.error('Failed to load clerks', e); toast.error('Failed to load clerks') })
      .finally(() => setLoadingClerks(false))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedClerkId) {
      toast.error('Please select your name')
      return
    }
    setLoading(true)

    try {
      const clerk = clerks.find(c => c.id === selectedClerkId)
      if (!clerk) {
        toast.error('Clerk not found')
        return
      }

      const { error } = await signIn(clerk.email, password)

      if (error) {
        toast.error(error.message || 'Failed to sign in')
      } else {
        toast.success(`Signed in as ${clerk.username}`)
        navigate(from, { replace: true })
      }
    } catch (error) {
      toast.error('An unexpected error occurred')
      console.error('Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: `linear-gradient(rgba(240,244,240,0.7), rgba(240,244,240,0.9)), url(${POS_BACKGROUND})` }}
    >
      <Card size="3" style={{ width: '400px', maxWidth: '90%' }}>
        <Flex direction="column" gap="4" p="4">
          <img src={THOMSONS_LOGO} alt="Thomson's" style={{ height: 72, width: 'auto', objectFit: 'contain', alignSelf: 'center' }} />
          <Heading size="6" align="center">
            Thomson's Landscaping &amp; Garden Center
          </Heading>
          <Text size="2" align="center" color="gray">
            Retail Management System
          </Text>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Select Your Name</Text>
                <Select.Root value={selectedClerkId} onValueChange={setSelectedClerkId} disabled={loadingClerks}>
                  <Select.Trigger placeholder={loadingClerks ? 'Loading clerks...' : 'Select your name'} />
                  <Select.Content>
                    {clerks.map(c => (
                      <Select.Item key={c.id} value={c.id}>{c.username}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>

              <Flex direction="column" gap="1">
                <Text size="2" weight="bold">Password</Text>
                <TextField.Root
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Flex>

              <Button
                type="submit"
                size="3"
                loading={loading}
                disabled={loading || !selectedClerkId}
              >
                Sign In
              </Button>
            </Flex>
          </form>

          <Text size="1" align="center" color="gray">
            © 2026 Thomsons Landscaping
          </Text>
        </Flex>
      </Card>
    </div>
  )
}
